/**
 * 訂單控管中心 Order Control Center workbench (S4.3) — replaces the codex
 * single-file board.
 *
 * ONE module, derive-never-restore: every lens is a projection over the
 * source stores via the S4.2 shared selectors; the workbench performs zero
 * writes and every action drills THROUGH to the owning module.
 *
 * - Lens tabs are ROUTE-DERIVED (`/auth/occ/:lens`, route-derived selection
 *   tenet); landing = 訂單進度總覽 Progress Overview (charter).
 * - 4 stat cards: 未結異常 by severity · 待出量 total · 待裝櫃 lines · 最久
 *   未處理 — each an affordance that drills into a lens (待出量 = the saved
 *   `balanceOnly` filter of 訂單明細, not a tab; ux-lens finding 5).
 * - The 裝櫃批次 lens is registered but its tab is hidden until W6
 *   (hide-don't-disable); referenced lenses are visibly read-only.
 */

import { Card, Col, Row, Tabs, Typography, theme } from 'antd';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageContainer from '@/components/common/PageContainer';
import { useOccBoard } from '@/hooks/useOcc';
import { useOccStore } from '@/stores/occStore';
import { OCC_DEFAULT_LENS, OCC_LENSES, isOccLensKey } from './lensRegistry';
import type { OccLensKey } from './lensRegistry';
import OverviewLens from './lenses/OverviewLens';
import OrderLinesLens from './lenses/OrderLinesLens';
import VendorConfirmLens from './lenses/VendorConfirmLens';
import ExceptionsLens from './lenses/ExceptionsLens';
import BatchLens from './lenses/BatchLens';
import InvoiceStatusLens from './lenses/InvoiceStatusLens';
import ReceiptStatusLens from './lenses/ReceiptStatusLens';

const { Text } = Typography;

const LENS_CONTENT: Record<OccLensKey, () => JSX.Element> = {
  overview: OverviewLens,
  lines: OrderLinesLens,
  'vendor-confirm': VendorConfirmLens,
  exceptions: ExceptionsLens,
  batches: BatchLens,
  'invoice-status': InvoiceStatusLens,
  'receipt-status': ReceiptStatusLens,
};

const OccBoardPage = () => {
  const { lens } = useParams<{ lens: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['occ', 'common']);
  const { token } = theme.useToken();

  const { stats } = useOccBoard();
  const { setLinesFilters, resetLinesFilters } = useOccStore();

  // Route-derived selection: an unknown lens key falls back to the landing.
  if (!isOccLensKey(lens)) {
    return <Navigate to={`/auth/occ/${OCC_DEFAULT_LENS}`} replace />;
  }

  const ActiveLens = LENS_CONTENT[lens];

  /** 待出量 / 待裝櫃 stat drill = the saved balanceOnly filter of 訂單明細. */
  const drillToOpenBalanceLines = () => {
    resetLinesFilters();
    setLinesFilters({ balanceOnly: true });
    navigate('/auth/occ/lines');
  };

  const statCardStyle = { cursor: 'pointer', height: '100%' };
  const statValueStyle = {
    fontSize: token.fontSizeHeading3,
    fontWeight: token.fontWeightStrong,
    fontVariantNumeric: 'tabular-nums' as const,
  };
  const statLabelStyle = { fontSize: token.fontSizeSM, color: token.colorTextSecondary };

  const severityBreakdown = stats
    ? (['high', 'medium', 'low'] as const)
        .map((severity) => `${t(`occ:occ.severity.${severity}`)} ${stats.openExceptions.bySeverity[severity]}`)
        .join(' · ')
    : '';

  return (
    <PageContainer>
      <div className="tw-mb-6">
        <h1 style={{ fontSize: token.fontSizeHeading2, fontWeight: token.fontWeightStrong }}>
          {t('occ:occ.title')}
        </h1>
      </div>

      {/* 4 stat cards — drills, not status noise */}
      <Row gutter={[12, 12]} style={{ marginBottom: token.marginLG }}>
        <Col xs={12} lg={6}>
          <Card
            size="small"
            hoverable
            style={statCardStyle}
            onClick={() => navigate('/auth/occ/exceptions')}
          >
            <div style={statLabelStyle}>{t('occ:occ.stats.openExceptions')}</div>
            <div style={{ ...statValueStyle, color: token.colorError }}>
              {stats?.openExceptions.total ?? '-'}
            </div>
            <Text style={statLabelStyle}>{severityBreakdown}</Text>
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small" hoverable style={statCardStyle} onClick={drillToOpenBalanceLines}>
            <div style={statLabelStyle}>{t('occ:occ.stats.orderBalance')}</div>
            <div style={statValueStyle}>{stats?.orderBalanceTotal ?? '-'}</div>
            <Text style={statLabelStyle}>{t('occ:occ.buckets.orderBalance')}</Text>
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small" hoverable style={statCardStyle} onClick={drillToOpenBalanceLines}>
            <div style={statLabelStyle}>{t('occ:occ.stats.linesToLoad')}</div>
            <div style={statValueStyle}>{stats?.linesToLoad ?? '-'}</div>
            <Text style={statLabelStyle}>{t('occ:occ.lenses.lines')}</Text>
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card
            size="small"
            hoverable
            style={statCardStyle}
            onClick={() => navigate('/auth/occ/exceptions')}
          >
            <div style={statLabelStyle}>{t('occ:occ.stats.oldestOpen')}</div>
            <div style={statValueStyle}>
              {stats == null
                ? '-'
                : stats.oldestOpenExceptionDays == null
                  ? t('occ:occ.stats.none')
                  : stats.oldestOpenExceptionDays}
            </div>
            <Text style={statLabelStyle}>
              {stats?.oldestOpenExceptionDays != null
                ? t('occ:occ.stats.daysValue', { days: stats.oldestOpenExceptionDays })
                : ''}
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Route-derived lens tabs (裝櫃批次 registered but hidden until W6) */}
      <Tabs
        activeKey={lens}
        onChange={(key) => navigate(`/auth/occ/${key}`)}
        items={OCC_LENSES.filter((def) => !def.tabHidden).map((def) => ({
          key: def.key,
          label: t(`occ:${def.i18nKey}`),
        }))}
      />

      <ActiveLens />
    </PageContainer>
  );
};

export default OccBoardPage;
